
import { flag } from '../../components/flags'
import { RevealCurtain, useRevealCountdown } from './RevealCountdown'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import { CEREMONY_SUBTITLE, EVENT_FOOTER, StageChrome, type StagePalette } from './StageChrome'
import type { AwardView } from '../admin/screenTypes'

/**
 * Best Traditional Attire on the stage screen — slide 25.
 *
 * Its own layout rather than the podium: the Committee approved a specific
 * one for this award — two portrait cards under a tabbed header, each with the
 * winner's attire and the flag of the country it comes from — and it is the
 * only award whose subject is a person rather than a booth. The header and
 * footer are the lucky draw's, through `StageChrome`.
 *
 * The male and female cards are revealed one at a time, male first. An
 * unrevealed card is drawn in full but holds nothing: the server sends no name
 * for it at all, so there is nothing in the payload to read ahead of the
 * announcement.
 */

/** Magenta-purple with rays from behind the cards. */
const ATTIRE_PALETTE: StagePalette = {
  background:
    'radial-gradient(ellipse at 50% 44%, rgba(255,196,255,0.16), transparent 56%), repeating-conic-gradient(from 210deg at 50% 46%, rgba(255,255,255,0.05) 0deg 6deg, transparent 6deg 17deg), linear-gradient(135deg, #5B1A6B 0%, #7A2280 48%, #3C1049 100%)',
  border: '#2A0E38',
  sparkle: '#FFD16A',
  sparkleAlt: '#F0A5E3',
}

/** Confetti, as still specks: the moving kind fires on reveal instead. */
const SPECKS = [
  { left: '7%', top: '12%', color: '#F4B63F', size: '0.9vmin', spin: '18deg' },
  { left: '22%', top: '26%', color: '#6FD3E0', size: '0.7vmin', spin: '-24deg' },
  { left: '34%', top: '9%', color: '#F2707F', size: '0.8vmin', spin: '40deg' },
  { left: '61%', top: '14%', color: '#8ED98A', size: '0.7vmin', spin: '12deg' },
  { left: '78%', top: '22%', color: '#F4B63F', size: '0.9vmin', spin: '-32deg' },
  { left: '92%', top: '38%', color: '#C79BF0', size: '0.8vmin', spin: '26deg' },
  { left: '4%', top: '62%', color: '#6FD3E0', size: '0.8vmin', spin: '-14deg' },
  { left: '17%', top: '83%', color: '#F2707F', size: '0.7vmin', spin: '34deg' },
  { left: '49%', top: '91%', color: '#F4B63F', size: '0.8vmin', spin: '-20deg' },
  { left: '72%', top: '86%', color: '#8ED98A', size: '0.7vmin', spin: '16deg' },
  { left: '88%', top: '70%', color: '#C79BF0', size: '0.9vmin', spin: '-38deg' },
] as const

export function AttireScreen({ award }: { award: AwardView }) {
  const male = award.results.find((r) => r.label === 'Male') ?? award.results[0]
  const female = award.results.find((r) => r.label === 'Female') ?? award.results[1]

  /*
   * The most recent reveal, by the time it was revealed rather than by
   * position — male is announced first, but a correction could land either
   * way round, and the count belongs to whichever card just opened.
   */
  const newest = [male, female]
    .filter((r) => r?.revealed && r.revealedAt)
    .sort((a, b) => (b!.revealedAt! > a!.revealedAt! ? 1 : -1))[0]

  const { pending, count } = useRevealCountdown(
    newest ? `${newest.label}:${newest.revealedAt}` : null,
    'purple',
    newest?.revealedAt ?? null,
    /*
     * The same award fanfare every other reveal gets, for both categories —
     * male and female are two winners of one award, not first and second.
     */
    () => stageSound.awardReveal(),
  )

  /** The card currently counting down, if any. */
  const counting = pending ? (pending.startsWith('Male') ? 'male' : 'female') : null

  return (
    <StageChrome
      palette={ATTIRE_PALETTE}
      title={award.title}
      subtitle={CEREMONY_SUBTITLE}
      status={
        award.complete
          ? 'Both winners revealed'
          : `Revealed ${award.revealedCount} of ${award.places}`
      }
      footerLeft={EVENT_FOOTER}
    >
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        {SPECKS.map((speck, index) => (
          <span
            key={index}
            className="absolute block rounded-[0.15vmin]"
            style={{
              left: speck.left,
              top: speck.top,
              width: speck.size,
              height: `calc(${speck.size} * 2)`,
              backgroundColor: speck.color,
              transform: `rotate(${speck.spin})`,
              opacity: 0.85,
            }}
          />
        ))}
      </div>

      <div className="flex w-full items-stretch justify-center gap-[3.5vmin]">
        <AttireCardView category="male" place={male} counting={counting === 'male'} />
        <AttireCardView
          category="female"
          place={female}
          counting={counting === 'female'}
        />
      </div>

      {counting && (
        <RevealCurtain
          title={award.title}
          place={counting === 'male' ? 'Male category' : 'Female category'}
          count={count}
        />
      )}
    </StageChrome>
  )
}

function AttireCardView({
  category,
  place,
  counting,
}: {
  category: 'male' | 'female'
  place: AwardView['results'][number] | undefined
  /** This card is the one being counted down right now. */
  counting: boolean
}) {
  const card = place?.attire ?? null

  /*
   * Held shut until the count finishes. The server has already sent the name
   * by then — it arrives in the payload that starts the count — so the card
   * knows who it is about to open on and simply does not say yet.
   */
  const revealed = place?.revealed === true && card != null && !counting

  return (
    <div
      key={counting ? 'counting' : (place?.revealedAt ?? 'waiting')}
      className={cn(
        'relative flex w-[38vmin] flex-col',
        /*
         * Each card sweeps in from its own side of the screen. Keyed above on
         * the reveal time, so it plays once, for the card that just opened,
         * and only after the curtain has counted down.
         */
        revealed && (category === 'male' ? 'reveal-sweep-left' : 'reveal-sweep-right'),
      )}
    >
      {/* The tab sits astride the card's top edge, as in the approved layout. */}
      <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2">
        <span className="block whitespace-nowrap rounded-[1.4vmin] bg-linear-to-b from-[#F7BC4A] to-[#E89A2A] px-[3.4vmin] py-[1.1vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.14em] text-[#3B2206] shadow-[0_4px_14px_rgba(40,10,50,0.4)]">
          {category === 'male' ? 'Male' : 'Female'} category
        </span>
      </div>

      {/*
        `flex-1` on the card, inside a row that stretches: the two cards are
        the same height whatever is in them. One winner's school name wraps to
        two lines and the other's does not, which left the male card visibly
        taller than the female one — two equal awards should not look unequal.
      */}
      <div
        className={cn(
          'mt-[2.2vmin] flex flex-1 flex-col items-center rounded-[2.2vmin] border-[0.45vmin] px-[2.4vmin] pb-[2.6vmin] pt-[4.4vmin] text-center',
          revealed
            ? 'border-[#EFAE3C] bg-white shadow-[0_14px_40px_rgba(30,6,45,0.45)]'
            : 'border-[#EFAE3C]/45 bg-white/12',
        )}
      >
        <Portrait category={category} revealed={revealed} counting={counting} />

        {revealed ? (
          <>
            <h2 className="mt-[2.2vmin] max-w-full text-[3.8vmin] font-extrabold leading-tight text-[#18275C]">
              {card.name}
            </h2>
            {/* Clipped rather than wrapped: this card sits beside its twin. */}
            {card.school && (
              <p className="mt-[0.9vmin] max-w-full truncate text-[2vmin] text-slate-500">
                {card.school}
              </p>
            )}
            {(card.attire || card.country) && (
              <p className="mt-[2vmin] flex items-center gap-[1vmin] rounded-full bg-[#F0E6FC] px-[2.2vmin] py-[1vmin] text-[1.85vmin] font-bold text-[#3A3E97]">
                {card.countryCode && (
                  <img
                    src={flag(card.countryCode, 80)}
                    alt=""
                    className="size-[2.1vmin] shrink-0 rounded-full object-cover ring-[0.15vmin] ring-white"
                  />
                )}
                {card.attire ? `Attire: ${card.attire}` : 'Attire'}
                {card.country && (
                  <>
                    <span className="text-[#3A3E97]/45">·</span>
                    {card.country}
                  </>
                )}
              </p>
            )}
            {/*
              The registration code, not the Student ID: it is the short number
              the winner can quote at the stage, and it reveals less.
            */}
            {card.regNo && (
              <p className="mt-[1.2vmin] font-mono text-[1.55vmin] tabular-nums text-slate-400">
                {card.regNo}
              </p>
            )}
          </>
        ) : counting ? (
          <p className="mt-[2.4vmin] text-[2.3vmin] font-extrabold uppercase tracking-[0.18em] text-[#FFD16A]">
            Announcing now
          </p>
        ) : (
          <p className="mt-[2.4vmin] text-[2.3vmin] font-semibold text-white/75">
            Not yet revealed
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * The crowned portrait circle.
 *
 * A silhouette, not a photograph: the register holds no photographs of
 * students, and collecting them for one award is not something the PDPA
 * notice covers. `attire_winners.photo_path` exists for the day the Committee
 * decides otherwise.
 */
function Portrait({
  category,
  revealed,
  counting,
}: {
  category: 'male' | 'female'
  revealed: boolean
  /** Lit gold while the curtain counts, but it shows no number of its own. */
  counting: boolean
}) {
  return (
    <div className="relative shrink-0">
      <svg
        viewBox="0 0 24 14"
        className="absolute -top-[2.1vmin] left-1/2 z-10 w-[5.4vmin] -translate-x-1/2"
        fill={revealed || counting ? '#EFAE3C' : 'rgba(239,174,60,0.5)'}
        aria-hidden
      >
        <path d="M1.6 13 .7 3.4l6.1 4.2L12 .8l5.2 6.8 6.1-4.2L22.4 13z" />
      </svg>

      <div
        className={cn(
          'grid size-[17vmin] place-items-center rounded-full border-[0.5vmin]',
          revealed
            ? 'border-[#EFAE3C] bg-linear-to-b from-[#EADCFB] to-[#D8C2F4]'
            : counting
              ? 'border-[#FFD16A] bg-[#2B0F3C]/70'
              : 'border-[#EFAE3C]/40 bg-white/10',
        )}
      >
        {revealed ? (
          <Silhouette category={category} />
        ) : (
          <span className="text-[5vmin] font-bold text-white/35">?</span>
        )}
      </div>
    </div>
  )
}

/** Restroom-pictogram shapes, so male and female read apart at 3 metres. */
function Silhouette({ category }: { category: 'male' | 'female' }) {
  return (
    <svg viewBox="0 0 48 66" className="h-[11vmin]" fill="#8B5CC7" aria-hidden>
      <circle cx="24" cy="10.5" r="9.5" />
      {category === 'male' ? (
        <path d="M16 23h16a6 6 0 0 1 6 6v14a2.6 2.6 0 0 1-5.2 0v-1h-1.4v21a2.4 2.4 0 0 1-4.8 0V44h-1.2v19a2.4 2.4 0 0 1-4.8 0V42h-1.4v1a2.6 2.6 0 0 1-5.2 0V29a6 6 0 0 1 6-6z" />
      ) : (
        <path d="M24 23c5.6 0 8.6 3.4 10 8.6L39 50h-6.4v13a2.4 2.4 0 0 1-4.8 0V50h-1.6v13a2.4 2.4 0 0 1-4.8 0V50H15l4.6-18.4C21 26.4 24 23 24 23z" />
      )}
    </svg>
  )
}
